import React from 'react'
import { Table, Flex, Button } from 'antd'
import {
  pinRowSelection,
  summaryColSpan,
  renderPinoutSummary,
} from '../../components/PinoutSelection'

import { ReactComponent as GroundIcon } from '../../assets/images/icons/svg/pinout/GND.svg'
import { ReactComponent as LINBusIcon } from '../../assets/images/icons/svg/pinout/LIN.svg'
import { ReactComponent as PowerIcon } from '../../assets/images/icons/svg/pinout/PWR.svg'
import { ReactComponent as CANBusIcon } from '../../assets/images/icons/svg/pinout/CAN.svg'
import CircuitColorIcon from '../CircuitColorIcon'
import { FormattedMessage, injectIntl } from 'react-intl'
import { ToolsButton } from '../../components/RelatedContent'
import { usePinSelection } from './PinSelectionContext'

const categoryIcons = new Map(
  Object.entries({
    ground: GroundIcon,
    power: PowerIcon,
    can_bus: CANBusIcon,
    lin_bus: LINBusIcon,
  }),
)

const PinoutTable = ({
  pinout,
  isLoading,
  isAllPins,
  setIsAllPins,
  showSummary = true,
  pageSize = 6,
  tools = null,
  toolsLoading = false,
}) => {
  const { selectable, selectedPins, onSelectedPinsChange, onDetailsClick } =
    usePinSelection()

  const columns = [
    {
      title: <FormattedMessage id="functionalDiagrams.pinoutTable.pin" />,
      dataIndex: 'pin',
      width: '15%',
      ellipsis: true,
    },
    {
      title: <FormattedMessage id="functionalDiagrams.pinoutTable.color" />,
      dataIndex: ['colors', 'letter_code'],
      width: '20%',
    },
    {
      title: '',
      dataIndex: ['colors', 'hex_triplets'],
      render: (text, record, index) => {
        if (text) {
          const [color_1, color_2] = text
          return <CircuitColorIcon firstColor={color_1} secondColor={color_2} />
        }
      },
      width: '10%',
    },
    {
      title: '',
      dataIndex: 'category',
      render: (text, record, index) => {
        const CategoryIcon = categoryIcons.get(text)
        return (
          CategoryIcon && (
            <Flex
              align="middle"
              className="block-diagram-circuit-category-icon"
            >
              <CategoryIcon />
            </Flex>
          )
        )
      },
      width: '10%',
    },
    {
      title: <FormattedMessage id="functionalDiagrams.pinoutTable.tools" />,
      dataIndex: 'tools',
      render: (text, record) => (
        <ToolsButton
          tools={[...(tools?.[record.pin] || []), ...(tools?.all || [])]}
          pin={record}
          isLoading={toolsLoading}
        />
      ),
    },
    {
      title: (
        <FormattedMessage id="functionalDiagrams.pinoutTable.description" />
      ),
      dataIndex: 'description',
      ellipsis: true,
    },
  ]

  if (!tools) {
    columns.splice(4, 1)
  }

  // Rows here are one per pin, so the pin/row mapping is the identity — but the
  // gesture handling is shared with the legacy pinout so both behave alike.
  const rowSelection = selectable
    ? pinRowSelection({
        rows: pinout,
        rowKey: 'pin',
        getPin: (record) => record.pin,
        selectedPins,
        onPinsChange: onSelectedPinsChange,
      })
    : undefined

  return (
    <Table
      size="small"
      rowKey={'pin'}
      dataSource={pinout}
      columns={columns}
      rowSelection={rowSelection}
      pagination={{
        pageSize: pageSize,
        hideOnSinglePage: true,
      }}
      loading={isLoading}
      summary={() =>
        showSummary &&
        renderPinoutSummary({
          colSpan: summaryColSpan(columns, selectable),
          isAllPins,
          setIsAllPins,
          loading: isLoading,
          children: selectable && (
            <Button
              type="primary"
              disabled={selectedPins.length === 0}
              onClick={onDetailsClick}
              data-testid="pinout-details-button"
            >
              <FormattedMessage id="functionalDiagrams.pinoutTable.details" />
            </Button>
          ),
        })
      }
      sticky
    />
  )
}

export default injectIntl(PinoutTable)
